import _toConsumableArray from "/Users/dom/src/deeplocal/maps-tycoon-2020/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import "core-js/modules/es6.regexp.split";
import "core-js/modules/es6.array.map";
import { comp, map, mapIndexed, range, str, take, transduce, zip } from "@thi.ng/transducers";
import { FN, MATH, MATH_N } from "./templates";
import { vop } from "./vop";
export var ARGS_V = "o,a";
export var ARGS_VV = "o,a,b";
export var ARGS_VVV = "o,a,b,c";
export var ARGS_VN = "o,a,n";
export var ARGS_VVN = "o,a,b,n";
export var SARGS_V = "io=0,ia=0,so=1,sa=1";
export var SARGS_VV = "io=0,ia=0,ib=0,so=1,sa=1,sb=1";
export var SARGS_VVV = "io=0,ia=0,ib=0,ic=0,so=1,sa=1,sb=1,sc=1";
export var DEFAULT_OUT = "!o&&(o=a);";
export var NEW_OUT = "!o&&(o=[]);";
/**
 * HOF array index lookup gen to provide optimized versions of:
 *
 * ```
 * lookup("a")(0) // a[ia]
 * lookup("a")(1) // a[ia * sa]
 * lookup("a")(2) // a[ia + 2 * sa]
 * ```
 *
 * @param sym
 */

var lookup = function lookup(sym) {
  return function (i) {
    return i > 1 ? "".concat(sym, "[i").concat(sym, "+").concat(i, "*s").concat(sym, "]") : i == 1 ? "".concat(sym, "[i").concat(sym, "+s").concat(sym, "]") : "".concat(sym, "[i").concat(sym, "]");
  };
};
/**
 * Infinite iterator of strided index lookups for `sym`.
 *
 * @param sym
 */


var indicesStrided = function indicesStrided(sym) {
  return map(lookup(sym), range());
};
/**
 * Infinite iterator of simple (non-strided) index lookups for `sym`.
 *
 * @param sym
 */


var indices = function indices(sym) {
  return map(function (i) {
    return "".concat(sym, "[").concat(i, "]");
  }, range());
};
/**
 * Code generator for loop-unrolled vector operations. Takes a vector
 * size `dim`, a code template function `tpl` and an array of symbol
 * names participating in the template. For each symbol, creates
 * iterator of index lookups (e.g. `a[0]` or `a[ia+k*sa]`), forms them
 * into tuples and passes them to template to generate code and joins
 * generated result with `opJoin` separator (default:
 * `""`).
 *
 * If the optional `ret` arg is not `null` (default `"a"`), appends a
 * `return` statement to the result array, using `ret` as return value.
 * Returns array of source code lines.
 *
 * The optional `pre` and `post` strings can be used to wrap the
 * generated code. `post` will be injected **before** the generated
 * return statement (if not suppressed).
 *
 * @param dim
 * @param tpl
 * @param syms
 * @param ret
 * @param opJoin
 * @param pre
 * @param post
 * @param strided
 */


var assemble = function assemble(dim, tpl, syms) {
  var ret = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "a";
  var opJoin = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : "";
  var pre = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : "";
  var post = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : "";
  var strided = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : false;
  return [pre, transduce(comp(take(dim), mapIndexed(function (i, x) {
    return tpl(x, i);
  })), str(opJoin), zip.apply(null, syms.split(",").map(strided ? indicesStrided : indices))), post, ret !== null ? "return ".concat(ret, ";") : ""];
};

var assembleG = function assembleG(tpl, syms) {
  var ret = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "a";
  var pre = arguments.length > 3 ? arguments[3] : undefined;
  var post = arguments.length > 4 ? arguments[4] : undefined;
  var strided = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  return [pre, "for(let i=a.length;--i>=0;) {", tpl(syms.split(",").map(strided ? function (x) {
    return "".concat(x, "[i").concat(x, "+i*s").concat(x, "]");
  } : function (x) {
    return "".concat(x, "[i]");
  })), "}", post, ret !== null ? "return ".concat(ret, ";") : ""];
};

export var defaultOut = function defaultOut(o, args) {
  return "!".concat(o, " && (").concat(o, "=").concat(args.split(",")[1], ");");
};
export var compile = function compile(dim, tpl, args) {
  var syms = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : args;
  var ret = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : "a";
  var opJoin = arguments.length > 5 ? arguments[5] : undefined;
  var pre = arguments.length > 6 ? arguments[6] : undefined;
  var post = arguments.length > 7 ? arguments[7] : undefined;
  var strided = arguments.length > 8 && arguments[8] !== undefined ? arguments[8] : false;
  return new Function(args, assemble(dim, tpl, syms, ret, opJoin, pre, post, strided).join(""));
};
export var compileHOF = function compileHOF(dim, fns, tpl, hofArgs, args) {
  var syms = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : args;
  var ret = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : "a";
  var opJoin = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : "";
  var pre = arguments.length > 8 ? arguments[8] : undefined;
  var post = arguments.length > 9 ? arguments[9] : undefined;
  var strided = arguments.length > 10 && arguments[10] !== undefined ? arguments[10] : false;
  return new Function(hofArgs, "return (".concat(args, ")=>{").concat(assemble(dim, tpl, syms, ret, opJoin, pre, post, strided).join(""), "}")).apply(void 0, _toConsumableArray(fns));
};
export var compileG = function compileG(tpl, args) {
  var syms = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : args;
  var ret = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "a";
  var pre = arguments.length > 4 ? arguments[4] : undefined;
  var post = arguments.length > 5 ? arguments[5] : undefined;
  var strided = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  return new Function(args, assembleG(tpl, syms, ret, pre, post, strided).join(""));
};
export var compileGHOF = function compileGHOF(fns, tpl, hofArgs, args) {
  var syms = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : args;
  var ret = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : "a";
  var pre = arguments.length > 6 ? arguments[6] : undefined;
  var post = arguments.length > 7 ? arguments[7] : undefined;
  var strided = arguments.length > 8 && arguments[8] !== undefined ? arguments[8] : false;
  return new Function(hofArgs, "return (".concat(args, ")=>{").concat(assembleG(tpl, syms, ret, pre, post, strided).join(""), "}")).apply(void 0, _toConsumableArray(fns));
};
export var defOp = function defOp(tpl) {
  var args = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ARGS_VV;
  var syms = arguments.length > 2 ? arguments[2] : undefined;
  var ret = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "o";
  var dispatch = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;
  var pre = arguments.length > 5 ? arguments[5] : undefined;
  syms = syms || args;
  pre = pre != null ? pre : defaultOut(ret, args);
  var fn = vop(dispatch);

  var $ = function $(dim) {
    return fn.add(dim, compile(dim, tpl, args, syms, ret, "", pre));
  };

  fn["default"](compileG(tpl, args, syms, ret, pre));
  return [fn, $(2), $(3), $(4)];
};
export var defFnOp = function defFnOp(op) {
  return defOp(FN(op), ARGS_V);
};
export var defHofOp = function defHofOp(op, tpl) {
  var args = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ARGS_V;
  var syms = arguments.length > 3 ? arguments[3] : undefined;
  var ret = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : "o";
  var dispatch = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 1;
  var pre = arguments.length > 6 ? arguments[6] : undefined;
  tpl = tpl || FN("op");
  syms = syms || args;
  pre = pre != null ? pre : defaultOut(ret, args);

  var $ = function $(dim) {
    return compileHOF(dim, [op], tpl, "op", args, syms, ret, "", pre);
  };

  var fn = vop(dispatch);
  fn["default"](compileGHOF([op], tpl, "op", args, syms, ret, pre));
  return [fn, $(2), $(3), $(4)];
};
export var defOpS = function defOpS(tpl) {
  var args = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "".concat(ARGS_VV, ",").concat(SARGS_VV);
  var syms = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ARGS_VV;
  var ret = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "o";
  var pre = arguments.length > 4 ? arguments[4] : undefined;
  var sizes = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [2, 3, 4];
  return sizes.map(function (dim) {
    return compile(dim, tpl, args, syms, ret, "", pre != null ? pre : defaultOut(ret, args), "", true);
  });
};
export var defMathOp = function defMathOp(op) {
  return defOp(MATH(op));
};
export var defMathNOp = function defMathNOp(op) {
  return defOp(MATH_N(op), ARGS_VN);
};